import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";

type CycleClinicEndpoint = paths["/cycles/{cycleId}/clinic"];

export type CycleClinic = ResponseBody<CycleClinicEndpoint["get"]>["data"];

export const cycleClinicKeys = {
  all: ["cycle-clinic"] as const,
  detail: (cycleId: string) => ["cycle-clinic", cycleId] as const,
};

export function useCycleClinic(cycleId: string | undefined) {
  const { client } = useApiClient();

  return useQuery<CycleClinic>({
    queryKey: cycleClinicKeys.detail(cycleId ?? ""),
    queryFn: async () => {
      if (!cycleId) throw new Error("A cycle is required to load its clinic.");
      const response = await client.GET("/cycles/{cycleId}/clinic", {
        params: {
          path: { cycleId },
        },
      });
      if (response.data) return response.data.data;
      throwApiError(
        response.error,
        "We couldn’t load the clinic connected to this cycle.",
        "useCycleClinic",
        response.response?.status,
      );
    },
    enabled: !!cycleId,
  });
}
